import type { AxiosError } from 'axios';

function flattenErrorMessages(value: unknown): string[] {
  if (typeof value === 'string') return [value];

  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenErrorMessages(item));
  }

  if (value && typeof value === 'object') {
    return Object.values(value).flatMap((item) => flattenErrorMessages(item));
  }

  return [];
}

/**
 * Quita trazas técnicas y datos sensibles del mensaje mostrado al usuario.
 * El backend ya envía los mensajes en español dentro de `error.message`;
 * acá solo limpiamos lo que no debe ver un usuario.
 *
 * Input:  'Stock insuficiente para "test" \n#0 /app/Http/Controllers/X.php(89): App\Http\Controllers\X->metodo()'
 * Output: 'Stock insuficiente para "test"'   (sin líneas de trace ni "(id N)")
 */
function sanitizeMessage(message: string): string {
  return (
    message
      .split('\n')
      // Input línea:  '#0 /app/Http/Controllers/StockProductionController.php(89): App\Http\Controllers\StockProductionController->deductIngredientAcrossWarehouses()'
      // Output línea: (eliminada) — es una traza interna, no para el usuario
      .filter((line) => !/(^#\d|\.php\(|App\\|Illuminate\\|GuzzleHttp\\)/.test(line))
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Si el mensaje viene envuelto en el trace de Guzzle del gateway
 * ("Client error: `POST ...` resulted in a `400 Bad Request` response:\n{json}"),
 * extrae y parsea el JSON con el error real del microservicio.
 *
 * Input:  'Client error: `POST /stock/...` resulted in a `400 Bad Request` response:\n{ "error": { "message": "..." } }'
 * Output: el objeto JSON parseado ({ error: { message: '...' } }) o null si no hay JSON
 */
function extractGuzzleBody(message: string): unknown | null {
  const match = message.match(/response:\s*(\{[\s\S]*\})/);
  if (!match) return null;

  try {
    return JSON.parse(match[1]);
  } catch {
    return null;
  }
}

/** Extrae el mensaje legible de un payload de error del backend ({message} y/o {error}). */
function messageFromPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;

  const body = payload as { message?: unknown; error?: unknown };

  if (typeof body.message === 'string' && body.message.trim()) {
    const guzzleBody = extractGuzzleBody(body.message);
    if (guzzleBody) {
      const inner = messageFromPayload(guzzleBody);
      if (inner) return inner;
    }
    return body.message.trim();
  }

  if (body.error !== undefined) {
    const errorBody = body.error;

    // El backend puede enviar { error: { message, cleanTrace, exception } }.
    // Usamos solo message/exception y descartamos cleanTrace (no es para el usuario).
    if (typeof errorBody === 'string') {
      return errorBody.trim();
    }

    if (errorBody && typeof errorBody === 'object') {
      const err = errorBody as { message?: unknown; exception?: unknown };

      if (typeof err.message === 'string' && err.message.trim()) {
        const guzzleBody = extractGuzzleBody(err.message);
        if (guzzleBody) {
          const inner = messageFromPayload(guzzleBody);
          if (inner) return inner;
        }
        return err.message.trim();
      }

      if (typeof err.exception === 'string' && err.exception.trim()) {
        return err.exception.trim();
      }

      // Errores de validación: { campo: string[] }
      return flattenErrorMessages(errorBody).join(' ');
    }
  }

  return null;
}

function messageFromString(raw: string): string | null {
  const guzzleBody = extractGuzzleBody(raw);
  if (guzzleBody) {
    const inner = messageFromPayload(guzzleBody);
    if (inner) return inner;
  }

  return raw.trim() || null;
}

/**
 * Detecta cuando el gateway envolvió un error HTML del microservicio externo
 * (ej. un 404 de Laravel). En ese caso `details` trae el HTML y `message`
 * es texto de status HTTP ("404 Not Found"), que no debe mostrarse al usuario.
 */
function isUpstreamHtmlError(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;

  const payload = data as { details?: unknown; message?: unknown };

  if (typeof payload.details === 'string' && /<!doctype|<html/i.test(payload.details)) {
    return true;
  }

  // Respaldo: el message tiene formato "404 Not Found" / "500 Server Error".
  if (typeof payload.message === 'string' && /^\d{3}\s+\w/i.test(payload.message.trim())) {
    return true;
  }
  return false;
}

/**
 * Detecta mensajes técnicos/internos que NO deben mostrarse al usuario
 * (errores de base de datos, SQL, traces de framework, datos de conexión).
 * En esos casos conviene devolver null y que el llamador use un mensaje genérico.
 *
 * Ej.: 'SQLSTATE[23000]: Integrity constraint violation: 1062 Duplicate entry
 *       'midiluca@gmail.com' for key 'users.users_email_unique'
 *       (Connection: mysql, Host: efan-dev-..., Database: efan_gateway, SQL: insert ...)'
 */
function isInternalErrorMessage(message: string): boolean {
  return /(SQLSTATE|Integrity constraint violation|Duplicate entry|for key\s|SQL:|PDOException|QueryException|Connection: mysql|Connection: pgsql|Host: |Database: |SQLSTATE\[)/i.test(
    message,
  );
}

/**
 * Extrae un mensaje legible a partir de un error de axios o del backend.
 * Soporta bodies del tipo:
 *  - { message: string }
 *  - { error: string }
 *  - { error: { field: string[] } } (errores de validación)
 * Y mensajes que llegan envueltos con el trace de Guzzle ("Client error: ...").
 * Los errores conocidos del módulo stock se muestran en español.
 * Devuelve null cuando no hay un mensaje aprovechable.
 */
export function getApiErrorMessage(error: unknown): string | null {
  if (!error) return null;

  const data = (error as AxiosError<unknown>)?.response?.data;

  // Errores HTML del upstream (microservicio caído / ruta inexistente):
  // mostramos un mensaje genérico en vez del status HTTP crudo.
  if (isUpstreamHtmlError(data)) {
    return null;
  }

  let message: string | null = null;

  if (typeof data === 'string') {
    message = messageFromString(data);
  } else {
    message = messageFromPayload(data);
  }

  if (!message && typeof error === 'string') {
    message = messageFromString(error);
  }

  if (!message && error instanceof Error) {
    message = messageFromString(error.message);
  }

  if (!message || isInternalErrorMessage(message)) {
    return null;
  }

  return sanitizeMessage(message);
}
